// "Ready text": ready-made text designs for any background (Ezriel, 2026-10-08).
// Each design restyles the lines that are already on the invitation (fonts, sizes, colours) and keeps
// the customer's wording; five per background: four house styles in colours that suit the background
// (light or dark artwork) and one in the background's own colours. Applying one twice gives the same
// result: sizes are taken from the template's original lines.
import { restackText } from '../../utils/restack';

// Which kind of line: the font / colour / size slot it takes in a design.
const ROLE_BY_LABEL = [
  ['head', /heading|title/i],
  ['intro', /invitation text|intro|wording/i],
  ['parents', /father|shva?ir|shver|in[- ]?law/i],
  ['week', /week|parsh|sedr/i],
  ['address', /address|street/i],
  ['place', /place|location|venue/i],
  ['name', /your name|^name$/i],
  ['small', /date line|closing|before place|top|reason|b"h/i],
];

export const textRole = (field, headId) => {
  const label = field.label || '';
  for (const [role, re] of ROLE_BY_LABEL) if (re.test(label)) return role;
  return field.id === headId ? 'head' : 'body';
};

// role -> font slot / colour slot
// The regular lines (opening, small lines, father / shvair) share one font and colour (Ezriel, 2026-10-09).
const FONT_SLOT = { head: 'head', intro: 'body', small: 'body', week: 'week', place: 'body', address: 'latin', name: 'name', parents: 'body', body: 'body' };
const COLOR_SLOT = { head: 'head', intro: 'body', small: 'body', week: 'body', place: 'body', address: 'body', name: 'name', parents: 'body', body: 'body' };

const PALETTES = {
  light: {
    gold: { head: '#8A6A1C', name: '#8A6A1C', body: '#2B2118', small: '#5A4A32' },
    navy: { head: '#1F2A44', name: '#1F2A44', body: '#273043', small: '#55607A' },
    wine: { head: '#7A1F2B', name: '#7A1F2B', body: '#3A2A22', small: '#6B4A42' },
    onyx: { head: '#1A1A1A', name: '#8A6A1C', body: '#1A1A1A', small: '#4A4640' },
    forest: { head: '#1F4D3A', name: '#1F4D3A', body: '#22302A', small: '#22302A' },
    silver: { head: '#3A3F45', name: '#3A3F45', body: '#2A2D31', small: '#2A2D31' },
    rose: { head: '#9A4A6A', name: '#9A4A6A', body: '#3A2A30', small: '#3A2A30' },
  },
  dark: {
    gold: { head: '#E2C06F', name: '#E2C06F', body: '#F4E9D0', small: '#E9DCC0' },
    navy: { head: '#FFFFFF', name: '#FFFFFF', body: '#F2F0EB', small: '#DCD6C8' },
    wine: { head: '#F3C9C4', name: '#F3C9C4', body: '#F7EDE6', small: '#E6D6CF' },
    onyx: { head: '#F7E6A6', name: '#FFFFFF', body: '#F4E9D0', small: '#E9DCC0' },
    forest: { head: '#CFE3C8', name: '#CFE3C8', body: '#F2F0EB', small: '#F2F0EB' },
    silver: { head: '#FFFFFF', name: '#FFFFFF', body: '#E6E8EA', small: '#E6E8EA' },
    rose: { head: '#F6C9D6', name: '#F6C9D6', body: '#F7EDE6', small: '#F7EDE6' },
  },
};

// Fonts: the first one this site has wins (the Fb / BA / KolOr fonts are the uploaded Hebrew fonts).
export const TEXT_DESIGNS = [
  {
    id: 'royal',
    name: 'Royal gold',
    palette: 'gold',
    fonts: {
      head: ['BA-Bikurim-Regular', 'Frank Ruhl Libre'],
      week: ['BA-Haketer-Regular', 'David Libre'],
      name: ['FbHarduf-Bold', 'Frank Ruhl Libre'],
      body: ['FbFrankReal-Regular', 'Frank Ruhl Libre'],
      small: ['HADDAR', 'David Libre'],
      latin: ['Frank Ruhl Libre'],
    },
    size: { head: 1.08, name: 1.04 },
    weight: { head: 'bold', name: 'bold' },
  },
  {
    id: 'modern',
    name: 'Modern navy',
    palette: 'navy',
    fonts: {
      head: ['Suez One'],
      week: ['Secular One'],
      name: ['Secular One'],
      body: ['FbFontext-Regular', 'Heebo'],
      small: ['FbFontext-Light', 'Heebo'],
      latin: ['Manrope'],
    },
    size: { head: 0.94, intro: 0.94, small: 0.95 },
    spacing: { small: 1 },
  },
  {
    id: 'soft',
    name: 'Soft & airy',
    palette: 'wine',
    fonts: {
      head: ['Bellefair'],
      week: ['Bellefair'],
      name: ['Bellefair'],
      body: ['David Libre'],
      small: ['David Libre'],
      latin: ['Playfair Display'],
    },
    size: { head: 1.1, name: 1.06, week: 1.04 },
    spacing: { head: 3, name: 1.5 },
  },
  {
    id: 'bold',
    name: 'Bold black & gold',
    palette: 'onyx',
    fonts: {
      head: ['KolOr-Regular', 'Suez One'],
      week: ['KolOr-Regular', 'Suez One'],
      name: ['KolOr-Regular', 'Secular One'],
      body: ['FbFontext-Regular', 'Rubik'],
      small: ['FbFontext-Light', 'Rubik'],
      latin: ['Manrope'],
    },
    size: { head: 1.02, week: 1.02 },
  },
  // 2026-10-09: the nice Kol fonts (heading, parsha and name in one font, every regular line in FbFontext)
  {
    id: 'kol-keter',
    name: 'Keter gold',
    palette: 'gold',
    fonts: { head: ['KolKeter-Regular', 'Frank Ruhl Libre'], week: ['KolKeter-Regular', 'David Libre'], name: ['KolKeter-Regular', 'Frank Ruhl Libre'], body: ['FbFontext-Regular', 'Heebo'], small: ['FbFontext-Regular', 'Heebo'], latin: ['Playfair Display'] },
    size: { head: 1.04 },
  },
  {
    id: 'kol-malchis',
    name: 'Malchis navy',
    palette: 'navy',
    fonts: { head: ['KolMalchis', 'Frank Ruhl Libre'], week: ['KolMalchis', 'David Libre'], name: ['KolMalchis', 'Frank Ruhl Libre'], body: ['FbFontext-Regular', 'Heebo'], small: ['FbFontext-Regular', 'Heebo'], latin: ['Playfair Display'] },
  },
  {
    id: 'kol-yakar',
    name: 'Yakar wine',
    palette: 'wine',
    fonts: { head: ['KolYakar-Regular', 'Bellefair'], week: ['KolYakar-Regular', 'Bellefair'], name: ['KolYakar-Regular', 'Bellefair'], body: ['FbFontext-Regular', 'David Libre'], small: ['FbFontext-Regular', 'David Libre'], latin: ['Playfair Display'] },
    size: { head: 1.04 },
  },
  {
    id: 'kol-chotam',
    name: 'Chotam forest',
    palette: 'forest',
    fonts: { head: ['KolChotam-Bold', 'Suez One'], week: ['KolChotam-Bold', 'Suez One'], name: ['KolChotam-Bold', 'Secular One'], body: ['FbFontext-Regular', 'Rubik'], small: ['FbFontext-Regular', 'Rubik'], latin: ['Manrope'] },
  },
  {
    id: 'kol-torah',
    name: 'Torah silver',
    palette: 'silver',
    fonts: { head: ['KolTorahPro-Regular', 'Frank Ruhl Libre'], week: ['KolTorahPro-Regular', 'David Libre'], name: ['KolTorahPro-Regular', 'Frank Ruhl Libre'], body: ['FbFontext-Regular', 'Heebo'], small: ['FbFontext-Regular', 'Heebo'], latin: ['Playfair Display'] },
    spacing: { head: 1 },
  },
  {
    id: 'kol-or-rose',
    name: 'Or rose',
    palette: 'rose',
    fonts: { head: ['KolOr-Regular', 'Suez One'], week: ['KolOr-Regular', 'Suez One'], name: ['KolOr-Regular', 'Secular One'], body: ['FbFontext-Regular', 'Heebo'], small: ['FbFontext-Regular', 'Heebo'], latin: ['Playfair Display'] },
  },
  {
    id: 'own',
    name: 'Design colours',
    palette: 'own',
    fonts: {
      head: ['Frank Ruhl Libre'],
      week: ['David Libre'],
      name: ['Frank Ruhl Libre'],
      body: ['FbFrankReal-Regular', 'Frank Ruhl Libre'],
      small: ['David Libre'],
      latin: ['Playfair Display'],
    },
    size: { head: 1.06 },
    weight: { head: 'bold', name: 'bold' },
    shadow: { head: true },
  },
];

const hexLum = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
};

const headOf = (fields) => fields.filter((f) => !f.hidden).reduce((b, f) => (!b || (f.font_size || 0) > (b.font_size || 0) ? f : b), null);

/** The template's own colours per slot, and whether its artwork is dark (light text). */
export const templateColors = (template, fields) => {
  const src = (template?.text_fields?.length ? template.text_fields : fields) || [];
  const headId = headOf(src)?.id;
  const pick = (slot) => src.find((f) => COLOR_SLOT[textRole(f, headId)] === slot)?.color;
  const body = pick('body') || '#2B2118';
  const head = pick('head') || body;
  const own = { head, name: pick('name') || head, body, small: pick('small') || body };
  const lum = hexLum(body);
  return { own, dark: lum !== null && lum > 0.6 };
};

// Glyphs added from the glyph browser, in whatever font was picked there. `ornament` is set on insert;
// older ones are recognised by the browser's default symbols font or the line it created ("Ornament").
const isOrnament = (f) =>
  Boolean(f.ornament) || f.font_family === 'Noto Sans Symbols 2' || (f.custom && f.label === 'Ornament');

/**
 * The current template's line for each invitation line: by id, else (after switching to another
 * background, whose lines have their own ids) by label, n-th line with a label <-> n-th template line
 * with it. Lines the customer added themselves have no template line.
 */
export const templateLines = (template, fields) => {
  const tl = template?.text_fields || [];
  const byId = Object.fromEntries(tl.map((t) => [t.id, t]));
  const byLabel = {};
  tl.forEach((t) => (byLabel[t.label || ''] = [...(byLabel[t.label || ''] || []), t]));
  const seen = {};
  const out = {};
  for (const f of fields) {
    if (f.custom || isOrnament(f)) continue;
    if (byId[f.id]) {
      out[f.id] = byId[f.id];
      continue;
    }
    const key = f.label || '';
    const n = seen[key] || 0;
    seen[key] = n + 1;
    if (key && byLabel[key]?.[n]) out[f.id] = byLabel[key][n];
  }
  return out;
};

/** Apply design `d` to the invitation's text lines (returns new text_fields). */
export const applyTextDesign = (d, { fields, template, families }) => {
  const { own, dark } = templateColors(template, fields);
  const colors = d.palette === 'own' ? own : PALETTES[dark ? 'dark' : 'light'][d.palette];
  const base = templateLines(template, fields);
  const headId = headOf(fields)?.id;
  const font = (slot, current) => (d.fonts[slot] || []).find((f) => families.has(f)) || current;
  const next = fields.map((f) => {
    if (isOrnament(f)) return f; // glyph ornaments keep their symbols font, size and place
    const role = textRole(f, headId);
    const fontSlot = FONT_SLOT[role];
    // sizes come from the template's line, so applying designs one after another never compounds;
    // the customer's own lines keep their size
    const size = base[f.id] ? Math.round((base[f.id].font_size || 24) * (d.size?.[role] || 1)) : f.font_size || 24;
    const out = {
      ...f,
      font_family: font(fontSlot, f.font_family),
      color: colors[COLOR_SLOT[role]] || f.color,
      font_size: Math.max(8, Math.min(400, size)),
      font_weight: d.weight?.[role] || 'normal',
      letter_spacing: d.spacing?.[role] || 0,
      shadow_color: d.shadow?.[role] ? (dark ? 'rgba(0,0,0,0.45)' : 'rgba(58,49,34,0.28)') : null,
    };
    if (d.shadow?.[role]) Object.assign(out, { shadow_blur: 6, shadow_x: 0, shadow_y: 2 });
    return out;
  });
  // stack only the template's wording again; ornaments and the customer's own lines stay where they are
  const wording = next.filter((f) => base[f.id]);
  const stacked = Object.fromEntries(
    restackText({ text_fields: wording, elements: [] }, template?.text_area).text_fields.map((f) => [f.id, f])
  );
  return next.map((f) => stacked[f.id] || f);
};

/** Back to the template's own fonts, sizes and colours (wording kept). */
export const originalTextDesign = ({ fields, template }) => {
  const base = templateLines(template, fields);
  const keys = ['font_family', 'font_size', 'color', 'font_weight', 'x', 'y', 'line_width', 'line_spacing'];
  return fields.map((f) => {
    const b = base[f.id];
    if (!b) return f;
    const restored = Object.fromEntries(keys.filter((k) => b[k] !== undefined).map((k) => [k, b[k]]));
    return { ...f, ...restored, letter_spacing: 0, shadow_color: null };
  });
};
